import { useState } from "react";
import { CheckCircle2, Landmark } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const hoy = () => new Date().toISOString().slice(0, 10);
const METODOS = ["transferencia", "efectivo", "cheque"];

/** Cierre del periodo: define el pago de cada empleado y registra el gasto de la nómina. */
export const CierreNomina = ({ periodo, onClose, onListo }) => {
  const pendientes = (periodo.lineas || []).filter((l) => !l.pagada);
  const [fechaPago, setFechaPago] = useState((periodo.fecha_pago || hoy()).slice(0, 10));
  const [metodoGeneral, setMetodoGeneral] = useState("transferencia");
  const [aviso, setAviso] = useState(true);
  const [registrarGasto, setRegistrarGasto] = useState(true);
  const [filas, setFilas] = useState(() => pendientes.map((l) => ({
    empleado_id: l.empleado_id, nombre: l.nombre, neto: l.neto, banco: l.banco,
    cuenta_banco: l.cuenta_banco, incluir: true, metodo: "transferencia", referencia: "",
  })));
  const [cerrando, setCerrando] = useState(false);

  const set = (id, cambios) => setFilas(filas.map(
    (f) => (f.empleado_id === id ? { ...f, ...cambios } : f)));

  const marcados = filas.filter((f) => f.incluir);
  const total = marcados.reduce((s, f) => s + Number(f.neto || 0), 0);
  const yaPagados = (periodo.lineas || []).filter((l) => l.pagada);

  const cerrar = async () => {
    if (marcados.length === 0 && yaPagados.length === 0) {
      return toast.error("Marque al menos un empleado para registrar su pago");
    }
    setCerrando(true);
    try {
      const { data } = await api.post(`/nomina/periodos/${periodo.id}/cerrar`, {
        registrar_gasto: registrarGasto, metodo_pago: metodoGeneral, fecha_pago: fechaPago,
        enviar_aviso: aviso,
        pagos: marcados.map((f) => ({
          empleado_id: f.empleado_id, metodo: f.metodo,
          referencia: f.referencia.trim() || null, fecha_pago: fechaPago, enviar_aviso: aviso,
        })),
      });
      toast.success(`Nómina cerrada: ${data.pagos} pago(s) registrados por ${money(data.total_pagado)}`);
      onListo();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
    setCerrando(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="modal-cierre-nomina">
        <DialogHeader>
          <DialogTitle className="font-display">Cerrar {periodo.nombre}</DialogTitle>
          <DialogDescription>
            Registre el pago de cada empleado. Al confirmar se guarda el gasto de la nómina y el
            empleado ve su volante en su portal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Fecha de pago</Label>
            <Input type="date" value={fechaPago} data-testid="cierre-fecha-pago"
                   onChange={(e) => setFechaPago(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Método para todos</Label>
            <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={metodoGeneral} data-testid="cierre-metodo-general"
                    onChange={(e) => {
                      setMetodoGeneral(e.target.value);
                      setFilas(filas.map((f) => ({ ...f, metodo: e.target.value })));
                    }}>
              {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex flex-col justify-end gap-2 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox checked={registrarGasto} data-testid="cierre-registrar-gasto"
                        onCheckedChange={(v) => setRegistrarGasto(!!v)} />
              Registrar el gasto en caja
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={aviso} data-testid="cierre-enviar-aviso"
                        onCheckedChange={(v) => setAviso(!!v)} />
              Avisar por correo a cada empleado
            </label>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border/60">
          <table className="w-full min-w-[760px] text-sm" data-testid="tabla-cierre-nomina">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Pagar</th>
                <th className="px-4 py-3">Empleado</th>
                <th className="px-4 py-3">Banco / cuenta</th>
                <th className="px-4 py-3">Método</th>
                <th className="px-4 py-3">Referencia</th>
                <th className="px-4 py-3">Neto</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.empleado_id} className="border-t border-border/60">
                  <td className="px-4 py-3">
                    <Checkbox checked={f.incluir} data-testid={`incluir-pago-${f.empleado_id}`}
                              onCheckedChange={(v) => set(f.empleado_id, { incluir: !!v })} />
                  </td>
                  <td className="px-4 py-3 font-medium">{f.nombre}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {f.banco || "—"}{f.cuenta_banco ? ` · ${f.cuenta_banco}` : ""}
                  </td>
                  <td className="px-4 py-3">
                    <select className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                            value={f.metodo} data-testid={`metodo-pago-${f.empleado_id}`}
                            onChange={(e) => set(f.empleado_id, { metodo: e.target.value })}>
                      {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <Input value={f.referencia} className="h-8 w-40 text-xs"
                           placeholder="No. transferencia / cheque"
                           data-testid={`referencia-pago-${f.empleado_id}`}
                           onChange={(e) => set(f.empleado_id, { referencia: e.target.value })} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{money(f.neto)}</td>
                </tr>
              ))}
              {yaPagados.map((l) => (
                <tr key={l.empleado_id} className="border-t border-border/60 bg-muted/20">
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-[10px]">pagado</Badge>
                  </td>
                  <td className="px-4 py-3 font-medium">{l.nombre}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground" colSpan={3}>
                    Ya pagado por {l.metodo_pago} el {l.fecha_pago}
                    {l.referencia_pago ? ` · ref. ${l.referencia_pago}` : ""}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{money(l.neto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm font-semibold" data-testid="total-cierre-nomina">
          <Landmark className="mr-2 inline h-4 w-4 text-primary" />
          {marcados.length} empleado(s) a pagar ahora por {money(total)}
        </p>

        <Button className="w-full rounded-full py-6" onClick={cerrar} disabled={cerrando}
                data-testid="confirmar-cierre-nomina">
          <CheckCircle2 className="mr-2 h-4 w-4" /> Registrar pagos y cerrar la nómina
        </Button>
      </DialogContent>
    </Dialog>
  );
};
